import { CreditCard, AlertCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/apiClient';

interface SubscriptionPlan {
  id: number;
  name: string;
  displayName: string;
  minLeases: number;
  maxLeases?: number;
  pricePerLease: number;
  isEnterprise: boolean;
  isActive: boolean;
}

export default function AdminSubscriptionsPage() {
  const { data: plans = [], isLoading, isError, error } = useQuery<SubscriptionPlan[]>({
    queryKey: ['admin', 'subscription-plans'],
    queryFn: () => api.get<SubscriptionPlan[]>('/admin/agencies/subscription-plans').then(r => r ?? []),
    staleTime: 60_000,
  });

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Subscription Plans"
        subtitle="Platform subscription tiers and pricing"
        icon={<CreditCard className="w-8 h-8" />}
      />

      {isError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">{(error as Error)?.message || 'Failed to load plans'}</div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {plans.map(plan => (
              <div
                key={plan.id}
                className={`bg-white rounded-lg shadow p-6 border-2 ${plan.isEnterprise ? 'border-primary' : 'border-transparent'}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{plan.displayName || plan.name}</h3>
                  <div className="flex gap-1">
                    {plan.isEnterprise && (
                      <span className="px-2 py-0.5 text-xs bg-primary text-white rounded-full">Enterprise</span>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full ${plan.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {plan.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-primary">{fmt(plan.pricePerLease)}</span>
                  <span className="text-sm text-gray-500 ml-1">/ lease / month</span>
                </div>

                <div className="text-sm text-gray-600">
                  <p>
                    {plan.minLeases} – {plan.maxLeases ?? '∞'} leases
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
            <strong>Pricing model:</strong> Agencies are charged based on their active lease count. Rates are per lease per month and decrease at higher tiers.
          </div>
        </>
      )}
    </div>
  );
}
